import { createStyles } from "@mantine/core";
import { Link, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { FieldInfoTooltip } from "../../components/FieldInfoTooltip";
import { Body, Heading } from "../../components/Typography";
import { DAILY_ORDER_LIMIT_TOOLTIP, QUEUED_ORDER_LIMIT_TOOLTIP } from "../../copy/orderLimitCopy";
import { PublicUrl } from "../../utils/link.util";

const useStyles = createStyles(() => ({
  banner: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[1],
    padding: tokens.spacing[4],
    borderRadius: tokens.radius.sm,
    background: tokens.color.semantic.bg.infoSubtlest,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[2],
  },
  steps: {
    margin: 0,
    paddingLeft: tokens.spacing[8],
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[1],
  },
  orderLimit: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacing[1],
  },
}));

/**
 * Info banner walking Customer Admins and Users through API setup, per the Developer Tools
 * design. Reads the organization's order limits from the shared current-user query.
 */
export const ApiSetupInstructionsBanner: React.FC = () => {
  const { classes } = useStyles();
  // Match useAuthUser's staleTime so this shared query isn't refetched on mount.
  const { data } = GraphQL.useDashboardUserCurrentQuery(undefined, { staleTime: Infinity });
  const ordersLimit = data?.dashboardUserCurrent?.customer?.settings?.ordersLimit;

  return (
    <div className={classes.banner} data-testid="api-setup-instructions">
      <div className={classes.header}>
        <IconV3 icon="info" size={24} color={tokens.color.semantic.icon.info} aria-hidden="true" />
        <Heading as="h2" size="xl">
          API Setup Instructions
        </Heading>
      </div>
      <ol className={classes.steps}>
        <li>
          <Body as="span" size="sm">
            To start using our API, copy the code snippet into your application.
          </Body>
        </li>
        <li>
          <Body as="span" size="sm">
            As you configure the customer order limit in your application, keep in mind your{" "}
            <OrderLimitCallout
              label="Daily Order Limit"
              formattedLimit={formatDailyOrderLimit(ordersLimit?.maxOrders)}
              tooltip={DAILY_ORDER_LIMIT_TOOLTIP}
            />{" "}
            and the{" "}
            <OrderLimitCallout
              label="Queued Order Limit"
              formattedLimit={formatQueuedOrderLimit(ordersLimit?.limitQueueSize)}
              tooltip={QUEUED_ORDER_LIMIT_TOOLTIP}
            />
            .
          </Body>
        </li>
        <li>
          <Body as="span" size="sm">
            Refer to our{" "}
            <Link href={PublicUrl.DOCS_HOME} size="small" openInNewTab data-testid="api-setup-docs-link">
              API Documentation
            </Link>{" "}
            for frequently asked questions.
          </Body>
        </li>
      </ol>
    </div>
  );
};

interface OrderLimitCalloutProps {
  label: string;
  /** Pre-formatted parenthetical, e.g. " (50)" — empty while the limit is unavailable. */
  formattedLimit: string;
  tooltip: string;
}

/** Bold order-limit callout with its explanatory info bubble, e.g. "Daily Order Limit (50) ⓘ". */
const OrderLimitCallout: React.FC<OrderLimitCalloutProps> = ({ label, formattedLimit, tooltip }) => {
  const { classes } = useStyles();
  return (
    <span className={classes.orderLimit}>
      <Body as="span" size="sm" weight="bold">
        {label}
        {formattedLimit}
      </Body>
      <FieldInfoTooltip label={label} content={tooltip} />
    </span>
  );
};

/** A daily limit of 0 means the organization is not limited (see CustomerView's maxOrders handling). */
function formatDailyOrderLimit(value?: number | null): string {
  if (value === null || value === undefined) return "";
  return value === 0 ? " (Unlimited)" : ` (${value})`;
}

/** Unlike the daily limit, a queued limit of 0 is a real zero-size queue, never "Unlimited". */
function formatQueuedOrderLimit(value?: number | null): string {
  if (value === null || value === undefined) return "";
  return ` (${value})`;
}
